import { useState } from 'react';
import { useCompany } from '../App';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import {
  Badge, Button, Card, Empty, ErrorBanner, Flash, Spinner, Tabs,
} from '../components/ui';
import { dateTime, enumLabel, shares } from '../lib/format';

/**
 * The review queue: changes an agent proposed, awaiting a human.
 *
 * Design follows from what a reviewer actually has to decide. They are being asked
 * to put something into the share register on a machine's suggestion, so the screen
 * leads with the evidence rather than the action:
 *
 *   - the quoted document text sits above the buttons, not behind a disclosure,
 *     because a proposal read out of a PDF is only as good as the quote
 *   - low confidence sorts first and is labelled, since that is where a human adds
 *     the most value
 *   - a plan built from several documents is approved as a unit, in order, because
 *     an issuance that references a share class the same plan creates cannot be
 *     approved on its own
 *   - rejecting demands a reason, so re-reading the same document does not
 *     re-propose something already refused with no record of why
 */

const KIND_LABELS = {
  CREATE_STAKEHOLDER: 'Add stakeholder',
  CREATE_SHARE_CLASS: 'Add share class',
  CREATE_EQUITY_PLAN: 'Add equity plan',
  CREATE_VESTING_SCHEDULE: 'Add vesting schedule',
  ISSUE_SECURITY: 'Issue security',
  UPDATE_STAKEHOLDER: 'Update stakeholder',
  UPDATE_SHARE_CLASS: 'Update share class',
  ADJUST_POOL: 'Adjust option pool',
  TRANSFER_SECURITY: 'Transfer shares',
  CANCEL_SECURITY: 'Cancel shares',
  RECORD_VALUATION: 'Record valuation',
};

const SOURCE_LABELS = {
  DOCUMENT_EXTRACTION: 'read from a document',
  AGENT_CHAT: 'asked for in chat',
  RULE: 'automatic check',
};

export default function Review() {
  const { companyId } = useCompany();
  const [status, setStatus] = useState('PENDING');
  const [flash, setFlash] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);

  const list = useAsync(() => api.proposals(companyId, { status }), [companyId, status]);

  async function act(fn, id, describe) {
    setBusy(id);
    setError(null);
    try {
      const result = await fn();
      setFlash(describe(result));
      list.reload();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(null);
    }
  }

  const approve = (p) =>
    act(
      () => api.approveProposal(companyId, p.id),
      p.id,
      () => `Applied: ${KIND_LABELS[p.kind] ?? p.kind}.`
    );

  const reject = (p) => {
    // A plain prompt rather than a modal: the reason is required by the API, and an
    // extra dialog layer here would be ceremony over a single sentence.
    const note = window.prompt(
      `Why are you rejecting this ${KIND_LABELS[p.kind] ?? p.kind}?\n\n` +
        'The reason is kept, so re-reading the same document will not silently ' +
        're-propose it.'
    );
    if (!note?.trim()) return;
    return act(
      () => api.rejectProposal(companyId, p.id, note.trim()),
      p.id,
      () => `Rejected: ${KIND_LABELS[p.kind] ?? p.kind}.`
    );
  };

  const approvePlan = (batchId, count) =>
    act(
      () => api.approveBatch(companyId, batchId),
      batchId,
      (r) =>
        r.complete
          ? `Applied all ${r.applied.length} steps.`
          : `Applied ${r.applied.length} of ${count}, then stopped at ${r.failedAt?.kind}. ` +
            'The rest are still pending.'
    );

  // Group by batch so a plan read from several documents is reviewed as one thing.
  const rows = list.data ?? [];
  const batches = new Map();
  const singles = [];
  for (const p of rows) {
    if (p.batch_id) {
      if (!batches.has(p.batch_id)) batches.set(p.batch_id, []);
      batches.get(p.batch_id).push(p);
    } else {
      singles.push(p);
    }
  }

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1>Review</h1>
          <p>
            Changes proposed from your documents and from the assistant. Nothing here has
            touched the cap table. Approving runs exactly the same code a manual entry
            would, so the ledger and the audit trail are identical either way.
          </p>
        </div>
        <div className="page-actions">
          <Button onClick={list.reload}>Refresh</Button>
        </div>
      </div>

      <Flash message={flash} onDone={() => setFlash(null)} />
      <ErrorBanner error={error} />

      <Tabs
        active={status}
        onChange={setStatus}
        tabs={[
          { key: 'PENDING', label: 'Awaiting review' },
          { key: 'APPROVED', label: 'Applied' },
          { key: 'REJECTED', label: 'Rejected' },
          { key: 'ALL', label: 'Everything' },
        ]}
      />

      {list.loading ? (
        <Spinner label="Loading the queue" />
      ) : list.error ? (
        <ErrorBanner error={list.error} onRetry={list.reload} />
      ) : rows.length === 0 ? (
        <Empty title={status === 'PENDING' ? 'Nothing awaiting review' : 'Nothing here'}>
          {status === 'PENDING'
            ? 'Upload formation documents and read them, or ask the assistant to make a change.'
            : null}
        </Empty>
      ) : (
        <>
          {[...batches.entries()].map(([batchId, steps]) => (
            <PlanCard
              key={batchId}
              batchId={batchId}
              steps={steps}
              busy={busy === batchId}
              onApproveAll={() => approvePlan(batchId, steps.length)}
              onApprove={approve}
              onReject={reject}
              stepBusy={busy}
            />
          ))}

          {singles.length > 0 ? (
            <Card title="Individual changes" padded={false}>
              <div className="proposal-list">
                {singles.map((p) => (
                  <ProposalRow
                    key={p.id}
                    proposal={p}
                    busy={busy === p.id}
                    onApprove={() => approve(p)}
                    onReject={() => reject(p)}
                  />
                ))}
              </div>
            </Card>
          ) : null}
        </>
      )}
    </div>
  );
}

function PlanCard({ batchId, steps, busy, onApproveAll, onApprove, onReject, stepBusy }) {
  const pending = steps.filter((s) => s.status === 'PENDING');
  const documents = [...new Set(steps.flatMap((s) => (s.document_title ? [s.document_title] : [])))];

  return (
    <Card
      title={`Plan from ${documents.length || 'your'} document${documents.length === 1 ? '' : 's'}`}
      actions={
        pending.length > 1 ? (
          <Button variant="primary" loading={busy} onClick={onApproveAll}>
            Apply all {pending.length} steps
          </Button>
        ) : null
      }
      padded={false}
    >
      <div className="plan-meta">
        {documents.length ? <span>{documents.join(' · ')}</span> : null}
        <span className="muted">
          Steps apply in order. A later step can depend on one above it, so applying the
          whole plan is usually right; applying one alone may fail if what it references
          does not exist yet.
        </span>
      </div>
      <div className="proposal-list">
        {steps.map((p) => (
          <ProposalRow
            key={p.id}
            proposal={p}
            ordered
            busy={stepBusy === p.id}
            onApprove={() => onApprove(p)}
            onReject={() => onReject(p)}
          />
        ))}
      </div>
    </Card>
  );
}

function ProposalRow({ proposal: p, busy, onApprove, onReject, ordered }) {
  const [showPayload, setShowPayload] = useState(false);
  const confidence = p.confidence === null ? null : Number(p.confidence);
  const decided = p.status !== 'PENDING';

  return (
    <div className={`proposal${decided ? ' is-decided' : ''}`}>
      <div className="proposal-head">
        <span className="proposal-kind">
          {ordered && p.apply_order !== null ? (
            <span className="proposal-order">{p.apply_order + 1}</span>
          ) : null}
          {KIND_LABELS[p.kind] ?? enumLabel(p.kind)}
        </span>

        <StatusBadge status={p.status} />

        {confidence !== null ? (
          <Badge tone={confidence < 0.7 ? 'warning' : 'neutral'}>
            {Math.round(confidence * 100)}% confident
          </Badge>
        ) : (
          <Badge tone="warning">confidence unknown</Badge>
        )}

        <span className="muted proposal-source">
          {SOURCE_LABELS[p.source] ?? enumLabel(p.source)}
          {p.model ? ` · ${p.model}` : ''}
        </span>
      </div>

      <div className="proposal-why">{p.rationale}</div>

      <Summary payload={p.payload} kind={p.kind} />

      {/* Evidence above the buttons, deliberately. A proposal read out of a document
          is only as good as the quote it came from. */}
      {p.citations?.length ? (
        <div className="citations">
          {p.citations.map((c, i) => (
            <blockquote key={i} className="citation">
              <span className="citation-text">“{c.quote}”</span>
              <span className="citation-source">
                {c.documentTitle ?? p.document_title ?? 'document'}
                {c.page ? `, page ${c.page}` : ''}
              </span>
            </blockquote>
          ))}
        </div>
      ) : null}

      <div className="proposal-foot">
        <button type="button" className="link-button" onClick={() => setShowPayload((v) => !v)}>
          {showPayload ? 'Hide' : 'Show'} exact values
        </button>

        {decided ? (
          <span className="muted">
            {p.status === 'APPROVED' ? 'Applied' : 'Rejected'} {dateTime(p.reviewed_at)}
            {p.reviewed_by_email ? ` by ${p.reviewed_by_email}` : ''}
            {p.review_note ? ` · ${p.review_note}` : ''}
          </span>
        ) : (
          <span className="proposal-actions">
            <Button size="sm" variant="ghost" loading={busy} onClick={onReject}>
              Reject
            </Button>
            <Button size="sm" variant="primary" loading={busy} onClick={onApprove}>
              Apply
            </Button>
          </span>
        )}
      </div>

      {showPayload ? <pre className="proposal-payload">{JSON.stringify(p.payload, null, 2)}</pre> : null}
    </div>
  );
}

function StatusBadge({ status }) {
  const tone =
    status === 'APPROVED' ? 'good' : status === 'REJECTED' ? 'critical' : status === 'SUPERSEDED' ? 'neutral' : 'info';
  return <Badge tone={tone}>{enumLabel(status)}</Badge>;
}

/**
 * The one or two figures that decide the review, in plain language.
 *
 * Share counts go through `shares()` so a large one is grouped rather than shown as
 * a raw string, and a `$ref` is rendered as what it is: a pointer to a step above,
 * not a value.
 */
function Summary({ payload, kind }) {
  if (!payload || typeof payload !== 'object') return null;

  const ref = (v) => (v && typeof v === 'object' && v.$ref ? `step "${v.$ref}"` : v);
  const parts = [];
  const add = (label, value) => {
    if (value === undefined || value === null || value === '') return;
    parts.push(
      <span key={label} className="summary-item">
        <span className="summary-label">{label}</span>
        <span className="summary-value">{value}</span>
      </span>
    );
  };

  if (payload.name) add('name', payload.name);
  if (payload.quantity) add('shares', shares(payload.quantity));
  if (payload.authorizedShares) add('authorized', shares(payload.authorizedShares));
  if (payload.initialPoolShares) add('pool', shares(payload.initialPoolShares));
  if (payload.pricePerShare) add('price', `$${payload.pricePerShare}`);
  if (payload.strikePrice) add('strike', `$${payload.strikePrice}`);
  if (payload.parValue) add('par', `$${payload.parValue}`);
  if (payload.securityType) add('type', enumLabel(payload.securityType));
  if (payload.classType) add('class', enumLabel(payload.classType));
  if (payload.issueDate) add('issued', payload.issueDate);
  if (payload.stakeholderId) add('holder', String(ref(payload.stakeholderId)).slice(0, 40));
  if (payload.shareClassId) add('into', String(ref(payload.shareClassId)).slice(0, 40));
  if (payload.patch) add('changes', Object.keys(payload.patch).join(', '));
  if (kind === 'CREATE_VESTING_SCHEDULE' && payload.totalMonths) {
    add('vesting', `${payload.totalMonths} months, ${payload.cliffMonths ?? 0} month cliff`);
  }

  if (parts.length === 0) return null;
  return <div className="proposal-summary">{parts}</div>;
}
